// src/components/management/OneOnOneSupportSystem.jsx (エラー対応・堅牢化版)
import React, { useState, useMemo, useEffect } from 'react';
import { Users, MessageCircle, TrendingUp, Lightbulb, Clock, Star, Target, AlertCircle, CheckCircle, Zap, Plus, Trash2, Send, History, ServerCrash } from 'lucide-react';

import { analyzeLeverageStrategy, generateHybridMission } from '../../services/aiAnalysisService';
import { trizLaws } from '../../data/trizLawDependencies';
import { saveOneOnOneRecord, getOneOnOneHistory } from '../../services/teamManagementService';

const OneOnOneSupportSystem = ({ managerProfile, memberProfiles }) => {
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  
  const [conversationTopics, setConversationTopics] = useState([]);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [actionItems, setActionItems] = useState([]);
  const [newActionItemText, setNewActionItemText] = useState('');
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // ▼▼▼ エラーハンドリング用の状態を追加 ▼▼▼
  const [historyError, setHistoryError] = useState(null);

  const memberAnalysis = useMemo(() => {
    if (!selectedMemberId || !memberProfiles[selectedMemberId]) return null;
    const profile = memberProfiles[selectedMemberId];
    if (!profile) return null;
    const { trizLawsMastery, neurodiversityProfile, talentPortfolio } = profile;
    const leverageStrategy = analyzeLeverageStrategy(trizLawsMastery);
    const hybridMission = generateHybridMission(trizLawsMastery, talentPortfolio);
    return {
      memberName: profile.username,
      strengths: Object.entries(trizLawsMastery || {}).filter(([,d])=>d.score>=4).sort(([,a],[,b])=>b.score-a.score).slice(0,3).map(([id,d])=>({id,name:trizLaws[id]?.name || id,score:d.score})),
      growthAreas: Object.entries(trizLawsMastery || {}).filter(([,d])=>d.score<3).sort(([,a],[,b])=>a.score-b.score).slice(0,3).map(([id,d])=>({id,name:trizLaws[id]?.name || id,score:d.score})),
      talentPortfolio: talentPortfolio || {},
      leverageStrategy,
      hybridMission,
    };
  }, [selectedMemberId, memberProfiles]);

  const generateConversationTopics = (analysis) => {
      if (!analysis) return []; const topics = [];
      if (analysis.leverageStrategy?.leveragePoint) topics.push({ category:'ai_strategy', priority:'high', title:'AIレバレッジ戦略', icon:Zap, description:`強み「${analysis.leverageStrategy.strength.name}」を活かす鍵は「${analysis.leverageStrategy.leveragePoint.name}」です。` });
      if (analysis.hybridMission) topics.push({ category:'hybrid_mission', priority:'high', title:'ハイブリッド成長ミッション', icon:Lightbulb, description:`強み「${analysis.hybridMission.strength.name}」と新要素「${analysis.hybridMission.newElement.name}」の組合せが提案されています。` });
      if (analysis.strengths.length > 0) topics.push({ category:'strength_utilization', priority:'medium', title:`強み「${analysis.strengths[0].name}」を活かすには？`, icon:Star });
      return topics;
  };
  
  useEffect(() => {
    const loadData = async () => {
      if (selectedMemberId) {
        setIsLoading(true);
        setHistoryError(null); // エラーをリセット
        try {
          // ▼▼▼ データベースアクセスをtry...catchで囲む ▼▼▼
          const historyData = await getOneOnOneHistory(selectedMemberId);
          setHistory(historyData);
          if (historyData.length > 0) {
            const latestRecord = historyData[0];
            setCurrentRecordId(latestRecord.id);
            setMeetingNotes(latestRecord.notes || '');
            setActionItems(latestRecord.actionItems || []);
          } else {
            setCurrentRecordId(null); setMeetingNotes(''); setActionItems([]);
          }
        } catch (error) {
            console.error("1on1履歴の取得に失敗:", error);
            setHistoryError("履歴の読み込みに失敗しました。データベースの権限設定を確認してください。");
        }
        
        if (memberAnalysis) {
          setConversationTopics(generateConversationTopics(memberAnalysis));
        }
        setIsLoading(false);
      } else {
        setConversationTopics([]); setHistory([]); setCurrentRecordId(null);
        setMeetingNotes(''); setActionItems([]); setHistoryError(null);
      }
    };
    loadData();
  }, [selectedMemberId]);

  const handleAddActionItem = () => {
    if (newActionItemText.trim() === '') return;
    const newItem = { id: `temp_${Date.now()}`, text: newActionItemText, status: 'todo', assignee: 'member', createdAt: new Date().toISOString() };
    setActionItems([...actionItems, newItem]); setNewActionItemText('');
  };

  const handleUpdateActionItem = (id, field, value) => setActionItems(actionItems.map(item => item.id === id ? { ...item, [field]: value } : item));
  const handleDeleteActionItem = (id) => setActionItems(actionItems.filter(item => item.id !== id));

  const handleSaveRecord = async () => {
    if (!selectedMemberId || isSaving) return; setIsSaving(true);
    try {
      const recordData = { managerId: managerProfile.uid, memberId: selectedMemberId, notes: meetingNotes, actionItems: actionItems };
      const savedRecordId = await saveOneOnOneRecord(currentRecordId, recordData);
      setCurrentRecordId(savedRecordId);
      alert('記録を保存しました！');
    } catch (error) { alert('記録の保存に失敗しました。'); } finally { setIsSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Users className="w-6 h-6 text-blue-600" />1on1 支援システム</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Object.entries(memberProfiles).filter(([,p]) => p && !p.role?.includes('master')).map(([id, p]) => (
            <div key={id} onClick={() => setSelectedMemberId(id)} className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${selectedMemberId === id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="text-center"><div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-2"><span className="text-white font-bold">{p.username?.[0]?.toUpperCase() || 'U'}</span></div><h3 className="font-medium text-gray-800">{p.username}</h3></div>
            </div>
          ))}
        </div>
      </div>

      {isLoading && <div className="text-center p-8">データを読み込み中...</div>}
      
      {!isLoading && selectedMemberId && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-yellow-600" />推奨トピック</h3>
              <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">{conversationTopics.map((topic, index) => (<div key={index} className={`p-4 rounded-lg border-l-4 ${topic.priority==='high'?'border-red-500 bg-red-50':topic.priority==='medium'?'border-yellow-500 bg-yellow-50':'border-blue-500 bg-blue-50'}`}><h4 className="font-medium flex items-center gap-2"><topic.icon className="w-5 h-5" />{topic.title}</h4><p className="text-sm my-2 pl-7">{topic.description}</p></div>))}</div>
            </div>
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><History className="w-5 h-5 text-gray-600" />過去の1on1履歴</h3>
              {/* ▼▼▼ エラーメッセージの表示を追加 ▼▼▼ */}
              {historyError ? (
                <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2"><ServerCrash className="w-5 h-5" /><span>{historyError}</span></div>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                  {history.length > 0 ? history.map(rec => (<div key={rec.id} className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100" onClick={() => { setCurrentRecordId(rec.id); setMeetingNotes(rec.notes || ''); setActionItems(rec.actionItems || []); }}><p className="font-medium text-sm text-gray-800">{rec.meetingDate ? rec.meetingDate.toLocaleDateString('ja-JP') : '日付不明'}</p><p className="text-xs text-gray-500 truncate">{rec.notes || '議事録なし'}</p></div>)) : <p className="text-sm text-gray-500">このメンバーとの履歴はありません。</p>}
                </div>
              )}
            </div>
          </div>
          {/* ... 右側の議事録部分は変更なし ... */}
        </div>
      )}
    </div>
  );
};

export default OneOnOneSupportSystem;
